import { useCallback, useEffect, useRef } from 'react';
import {
  apiClientFactory,
  healthUrlFor,
  isProbeCacheFresh,
  pingHealth,
  reachabilityStore,
  readLastHealthyAt,
  writeLastHealthyAt,
} from '@doccareplus/api-client';

/** Show the "waking up" overlay only if the probe is still pending after this long. */
const GRACE_MS = 2000;
/**
 * Generous ceiling for a single probe: a cold-started (sleeping) server holds the
 * connection while it boots (~30–60s), so we wait rather than mistaking "slow"
 * for "failed". A fast rejection (connection refused / dead) resolves well before
 * this and is treated as failure immediately.
 */
const PROBE_TIMEOUT_MS = 60_000;

export interface ServerWakeProbe {
  /** Force a fresh `/health` probe, ignoring the cache (the offline "Try again" action). */
  retry: () => void;
}

/**
 * Proactive cold-start wake probe for the Render free tier.
 *
 * On mount it pings `/health` — at most once per 30-minute cache window — across
 * the configured servers and drives the shared reachability store:
 * - probe still pending after 2s (server waking) → `reconnecting` ("waking up")
 * - a server answers → `online` (cached; that server is activated)
 * - every server fails fast → `offline` ("not reachable"), no long wait
 *
 * A single in-flight probe is enforced, so React StrictMode's double-invoked mount
 * effect hits `/health` only once. `retry()` forces a
 * fresh probe regardless of the cache. Complements the reactive reachability the
 * api-client derives from real API traffic; both feed the same store.
 */
export function useServerWakeProbe(): ServerWakeProbe {
  const inFlight = useRef(false);

  const runProbe = useCallback(async (force: boolean) => {
    if (inFlight.current) return; // dedupe StrictMode double-invoke / concurrent triggers

    if (!force && isProbeCacheFresh(Date.now(), readLastHealthyAt())) {
      reachabilityStore.set('online');
      return;
    }

    inFlight.current = true;
    // A forced retry shows "waking up" immediately; the automatic probe waits out
    // the grace so a warm server never flashes the overlay.
    if (force) reachabilityStore.set('reconnecting');
    const graceTimer = setTimeout(() => reachabilityStore.set('reconnecting'), GRACE_MS);

    try {
      for (const url of apiClientFactory.getServerUrls()) {
        const ok = await pingHealth(healthUrlFor(url), PROBE_TIMEOUT_MS);
        if (ok) {
          writeLastHealthyAt(Date.now());
          apiClientFactory.activateServer(url);
          reachabilityStore.set('online');
          return;
        }
      }
      // Every configured server failed → unreachable.
      reachabilityStore.set('offline');
    } finally {
      clearTimeout(graceTimer);
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    void runProbe(false);
  }, [runProbe]);

  const retry = useCallback(() => {
    void runProbe(true);
  }, [runProbe]);

  return { retry };
}
